import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  DataTable,
  SearchWithFilter,
  type DataTableColumn,
  type DataTableEmptyState,
  type DataTableRow,
  type FilterSelection,
} from '@enamelai/design-system'
import { FILTER_SHOW_ALL } from '@constants/Notifications'
import './PatientProfileDataTable.css'

function toHeaderSentenceCase(text: string): string {
  const lower = text.toLowerCase()
  const idx = lower.search(/[a-z]/)
  if (idx === -1) return lower
  return (
    lower.slice(0, idx) + lower.charAt(idx).toUpperCase() + lower.slice(idx + 1)
  )
}

function buildInitialFilterSelection(
  filters: Record<string, string[]>,
): FilterSelection {
  const sel: FilterSelection = {}
  for (const [cat, opts] of Object.entries(filters)) {
    if (opts.includes(FILTER_SHOW_ALL)) sel[cat] = [FILTER_SHOW_ALL]
  }
  return sel
}

function defaultSearchTextFromRow<TRow>(
  row: TRow,
  columns: PatientProfileDataTableColumn<TRow>[],
): string {
  return columns
    .filter((c) => {
      const k = String(c.key)
      return k !== 'actions' && !k.startsWith('__')
    })
    .map((c) => {
      const v = (row as Record<string, unknown>)[String(c.key)]
      if (v == null) return ''
      return String(v)
    })
    .join(' ')
}

function rowMatchesFilters<TRow>(
  row: TRow,
  selection: FilterSelection,
  categoryToKey: Record<string, keyof TRow | string> | undefined,
  valueFromRow: Record<string, (row: TRow) => string> | undefined,
  filterCategories: string[],
): boolean {
  for (const category of filterCategories) {
    const selected = selection[category]
    if (selected == null || selected.length === 0) continue
    if (selected.length === 1 && selected[0] === FILTER_SHOW_ALL) continue
    const effective = selected.filter((s) => s !== FILTER_SHOW_ALL)
    if (effective.length === 0) continue

    let val: string
    if (valueFromRow?.[category]) {
      val = valueFromRow[category](row)
    } else {
      const key = categoryToKey?.[category] ?? category
      const raw = (row as Record<string, unknown>)[String(key)]
      val = raw == null ? '' : String(raw)
    }
    if (!effective.includes(val)) return false
  }
  return true
}

export type PatientProfileDataTableColumn<TRow> = {
  key: keyof TRow | string
  label: string
  width?: number | string
  align?: 'left' | 'center' | 'right'
  /** Keep the cell on one line and truncate overflow with an ellipsis. */
  ellipsis?: boolean
  render?: (row: TRow) => React.ReactNode
  sortable?: boolean
  sortValue?: (row: TRow) => string | number
}

export type PatientProfileDataTableSearchConfig<TRow> = {
  filters?: Record<string, string[]>
  filterLabels?: Record<string, string>
  placeholder?: string
  className?: string
  filterCategoryToRowKey?: Record<string, keyof TRow | string>
  filterValueFromRow?: Record<string, (row: TRow) => string>
  searchTextFromRow?: (row: TRow) => string
}

export type PatientProfileDataTableProps<TRow> = {
  columns: PatientProfileDataTableColumn<TRow>[]
  rows: TRow[]
  rowKey: (row: TRow, index: number) => string
  pageSizeOptions?: number[]
  initialPageSize?: number
  showPagination?: boolean
  tableTitle?: React.ReactNode
  headerEndSlot?: React.ReactNode
  searchWithFilter?: PatientProfileDataTableSearchConfig<TRow>
  emptyState?: DataTableEmptyState
  className?: string
  enableSorting?: boolean
  /** Column and direction applied before the user sorts (e.g. name A-Z). */
  defaultSort?: { key: string; dir: 'asc' | 'desc' }
}

function PatientProfileDataTable<TRow>({
  columns,
  rows,
  rowKey,
  pageSizeOptions = [10, 25, 50, 100],
  initialPageSize = 10,
  showPagination = false,
  tableTitle,
  headerEndSlot,
  searchWithFilter,
  emptyState,
  className = '',
  enableSorting = true,
  defaultSort,
}: PatientProfileDataTableProps<TRow>): React.ReactElement {
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(initialPageSize)
  const [sort, setSort] = useState<{
    key: string
    dir: 'asc' | 'desc'
  } | null>(defaultSort ?? null)
  const [searchQuery, setSearchQuery] = useState('')
  const [filterSelection, setFilterSelection] = useState<FilterSelection>({})

  const filters = searchWithFilter?.filters
  const filtersKey = JSON.stringify(filters ?? {})
  useEffect(() => {
    const next: Record<string, string[]> = JSON.parse(filtersKey)
    setSearchQuery('')
    setFilterSelection(buildInitialFilterSelection(next))
    setCurrentPage(1)
  }, [filtersKey])

  const filterCategoryIds = useMemo(
    () => Object.keys(JSON.parse(filtersKey) as Record<string, string[]>),
    [filtersKey],
  )

  const filteredRows = useMemo(() => {
    if (searchWithFilter == null) return rows
    const q = searchQuery.trim().toLowerCase()
    let out = rows
    if (q) {
      const textFn =
        searchWithFilter.searchTextFromRow ??
        ((r: TRow) => defaultSearchTextFromRow(r, columns))
      out = out.filter((r) => textFn(r).toLowerCase().includes(q))
    }
    if (filterCategoryIds.length > 0) {
      out = out.filter((r) =>
        rowMatchesFilters(
          r,
          filterSelection,
          searchWithFilter.filterCategoryToRowKey,
          searchWithFilter.filterValueFromRow,
          filterCategoryIds,
        ),
      )
    }
    return out
  }, [
    rows,
    searchWithFilter,
    searchQuery,
    filterSelection,
    filterCategoryIds,
    columns,
  ])

  const sortedRows = useMemo(() => {
    if (!enableSorting || sort == null) return filteredRows
    const col = columns.find((c) => String(c.key) === sort.key)
    if (!col || col.sortable === false) return filteredRows

    const getVal = (row: TRow): string | number => {
      if (col.sortValue) return col.sortValue(row)
      const v = (row as Record<string, unknown>)[String(col.key)]
      if (typeof v === 'number') return v
      if (v == null) return ''
      return String(v)
    }

    const copy = [...filteredRows]
    copy.sort((a, b) => {
      const va = getVal(a)
      const vb = getVal(b)
      const cmp =
        typeof va === 'number' && typeof vb === 'number'
          ? va - vb
          : String(va).localeCompare(String(vb), undefined, {
              numeric: true,
              sensitivity: 'base',
            })
      return sort.dir === 'asc' ? cmp : -cmp
    })
    return copy
  }, [filteredRows, sort, columns, enableSorting])

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / pageSize))

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages)
  }, [currentPage, totalPages])

  const pagedRows = useMemo(() => {
    if (!showPagination) return sortedRows
    const start = (currentPage - 1) * pageSize
    return sortedRows.slice(start, start + pageSize)
  }, [showPagination, currentPage, pageSize, sortedRows])

  // DataTable rows only carry display values, so cells look the source row up by id.
  const { tableRows, rowsById } = useMemo(() => {
    const byId = new Map<string, TRow>()
    const list: DataTableRow[] = pagedRows.map((row, index) => {
      const id = rowKey(row, index)
      byId.set(id, row)
      return { id }
    })
    return { tableRows: list, rowsById: byId }
  }, [pagedRows, rowKey])

  const tableColumns = useMemo(
    (): DataTableColumn[] =>
      columns.map((column) => ({
        id: String(column.key),
        label: toHeaderSentenceCase(column.label),
        sortable: enableSorting && column.sortable !== false,
        width: column.width,
        align: column.align,
        renderCell: (tableRow) => {
          const row = rowsById.get(tableRow.id)
          if (row === undefined) return null
          let content: React.ReactNode
          if (column.render) {
            content = column.render(row)
          } else {
            const value = (row as Record<string, unknown>)[String(column.key)]
            content = value == null ? '' : String(value)
          }
          if (!column.ellipsis) return content
          return (
            <span
              className="patient-profile-data-table__ellipsis"
              title={
                typeof content === 'string' || typeof content === 'number'
                  ? String(content)
                  : undefined
              }
            >
              {content}
            </span>
          )
        },
      })),
    [columns, enableSorting, rowsById],
  )

  const handleSort = useCallback((columnId: string): void => {
    setCurrentPage(1)
    setSort((prev) =>
      prev?.key === columnId
        ? { key: columnId, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key: columnId, dir: 'asc' },
    )
  }, [])

  const handleSearchChange = useCallback((value: string): void => {
    setSearchQuery(value)
    setCurrentPage(1)
  }, [])

  const handleFilterChange = useCallback((next: FilterSelection): void => {
    setFilterSelection(next)
    setCurrentPage(1)
  }, [])

  const handlePageSizeChange = useCallback((next: number): void => {
    setPageSize(next)
    setCurrentPage(1)
  }, [])

  const showHeaderRow =
    tableTitle != null || headerEndSlot != null || searchWithFilter != null

  return (
    <div className={`patient-profile-data-table ${className}`.trim()}>
      {showHeaderRow ? (
        <div className="patient-profile-data-table__header">
          {tableTitle != null ? (
            <h2 className="patient-profile-data-table__title">{tableTitle}</h2>
          ) : null}
          {searchWithFilter != null ? (
            <SearchWithFilter
              className={`patient-profile-data-table__search ${
                searchWithFilter.className ?? ''
              }`.trim()}
              placeholder={searchWithFilter.placeholder ?? 'Search'}
              value={searchQuery}
              onChange={handleSearchChange}
              filters={filters ?? {}}
              filterLabels={searchWithFilter.filterLabels}
              selectedFilters={filterSelection}
              onFilterChange={handleFilterChange}
              showFilter={filterCategoryIds.length > 0}
            />
          ) : null}
          {headerEndSlot != null ? (
            <div className="patient-profile-data-table__header-end">
              {headerEndSlot}
            </div>
          ) : null}
        </div>
      ) : null}
      <DataTable
        columns={tableColumns}
        rows={tableRows}
        showActions={false}
        state={tableRows.length === 0 && emptyState ? 'empty' : 'default'}
        emptyState={emptyState}
        sortColumnId={sort?.key}
        sortDirection={sort?.dir}
        onSort={enableSorting ? handleSort : undefined}
        pagination={
          showPagination
            ? {
                currentPage,
                totalPages,
                pageSize,
                pageSizeOptions,
                onPageChange: setCurrentPage,
                onPageSizeChange: handlePageSizeChange,
              }
            : undefined
        }
      />
    </div>
  )
}

export default PatientProfileDataTable
